import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { NewItemInput, RimsType, TireSet, TireUsage } from "@skladiscenje/contracts";

import type { Capabilities } from "../api/capabilities.js";
import { useApiClient } from "../api/ApiClientContext.js";
import { createItem, deleteItem, markPickedUp, restoreItem, revertPickup, updateItem } from "../api/items.js";
import type { WireItem, WireSeason } from "../api/types.js";
import { Dialog } from "../components/Dialog.js";
import { describeApiError } from "../domain/errors.js";
import { formatDateTime, seasonLabel } from "../domain/format.js";
import { asItemId } from "../domain/ids.js";
import { useSeasons } from "../hooks/useSeasons.js";
import { selectPrintService } from "../print/printService.js";
import { AutocompleteInput } from "./AutocompleteInput.js";
import { PinField } from "./PinField.js";
import { emptyTireSet, TireSetFields, type TireSetErrors, type TireSetFormValue } from "./TireSetFields.js";
import { SwapPicker } from "./SwapPicker.js";
import { useSwapCandidates } from "./useSwapCandidates.js";
import "./form.css";

interface FormFields {
  lastname: string;
  name: string;
  phone: string;
  carMaker: string;
  registrationNumber: string;
  set1: TireSetFormValue;
  secondSetEnabled: boolean;
  set2: TireSetFormValue;
  notes: string;
  washing: boolean;
  printRequested: boolean;
}

function tireSetToForm(set: TireSet): TireSetFormValue {
  return {
    dimension: set.dimension ?? "",
    quantity: set.quantity ? String(set.quantity) : "",
    maker: set.maker ?? "",
    usage: set.usage ?? "",
    rimsType: set.rimsType ?? "",
  };
}

function initialFields(item: WireItem | null): FormFields {
  const set1 = item?.tireSets.find((s) => s.ordinal === 1);
  const set2 = item?.tireSets.find((s) => s.ordinal === 2);
  return {
    lastname: item?.lastname ?? "",
    name: item?.name ?? "",
    phone: item?.phone ?? "",
    carMaker: item?.carMaker ?? "",
    registrationNumber: item?.registrationNumber ?? "",
    set1: set1 ? tireSetToForm(set1) : emptyTireSet(),
    secondSetEnabled: set2 !== undefined,
    set2: set2 ? tireSetToForm(set2) : emptyTireSet(),
    notes: item?.notes ?? "",
    washing: item?.washing ?? false,
    printRequested: true,
  };
}

function toApiTireSet(ordinal: 1 | 2, value: TireSetFormValue): NewItemInput["tireSets"][number] {
  return {
    ordinal,
    dimension: value.dimension.trim(),
    quantity: (Number(value.quantity) || 0) as NewItemInput["tireSets"][number]["quantity"],
    maker: value.maker.trim(),
    usage: (value.usage || "100%") as TireUsage,
    rimsType: (value.rimsType || "BREZ") as RimsType,
  };
}

/**
 * Which required fields are empty, checked on the client so a save attempt can
 * highlight them in place rather than round-tripping to the server and printing
 * the whole list of zod failures at the bottom of the form. Mirrors
 * `newItemInput` (contracts): identity (`lastname`/`name`/`carMaker`) plus each
 * enabled set's `maker`/`dimension`/`quantity`. The server still validates — this
 * is the friendlier front door to the same rules, not a replacement for them.
 */
interface FieldErrors {
  lastname?: boolean;
  name?: boolean;
  carMaker?: boolean;
  set1?: TireSetErrors;
  set2?: TireSetErrors;
}

function setErrors(value: TireSetFormValue): TireSetErrors | undefined {
  const errors: TireSetErrors = {};
  if (value.maker.trim() === "") errors.maker = true;
  if (value.dimension.trim() === "") errors.dimension = true;
  if (!(Number(value.quantity) >= 1)) errors.quantity = true;
  return Object.keys(errors).length > 0 ? errors : undefined;
}

function computeFieldErrors(fields: FormFields): FieldErrors {
  const errors: FieldErrors = {};
  if (fields.lastname.trim() === "") errors.lastname = true;
  if (fields.name.trim() === "") errors.name = true;
  if (fields.carMaker.trim() === "") errors.carMaker = true;
  const set1 = setErrors(fields.set1);
  if (set1) errors.set1 = set1;
  if (fields.secondSetEnabled) {
    const set2 = setErrors(fields.set2);
    if (set2) errors.set2 = set2;
  }
  return errors;
}

/** The id of the first flagged field in DOM order, so a save attempt can move focus straight to it. */
function firstInvalidId(errors: FieldErrors): string | null {
  if (errors.lastname) return "lastname";
  if (errors.name) return "name";
  if (errors.carMaker) return "carMaker";
  if (errors.set1?.maker) return "set1-maker";
  if (errors.set1?.dimension) return "set1-dimension";
  if (errors.set1?.quantity) return "set1-quantity";
  if (errors.set2?.maker) return "set2-maker";
  if (errors.set2?.dimension) return "set2-dimension";
  if (errors.set2?.quantity) return "set2-quantity";
  return null;
}

function seasonDisplayLabel(item: WireItem | null, seasons: WireSeason[] | undefined): string {
  if (!seasons) return "";
  const season = item ? seasons.find((s) => s.id === item.seasonId) : seasons.find((s) => s.isOpen);
  return season ? seasonLabel(season) : "";
}

interface Confirmation {
  item: WireItem;
  swappedOut: WireItem | null;
  printMessage: string | null;
}

/**
 * The counter's entry form — replaces `AddItemWindow.xaml`. Handles both new
 * intake (`mode: "create"`) and editing a stored item (`mode: "edit"`).
 */
export function ItemForm({
  mode,
  item,
  capabilities,
  onDone,
}: {
  mode: "create" | "edit";
  item: WireItem | null;
  capabilities: Capabilities;
  onDone: () => void;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const seasonsQuery = useSeasons();

  const [fields, setFields] = useState<FormFields>(() => initialFields(item));
  const [swapOutItemId, setSwapOutItemId] = useState<string | null>(null);
  const [pickedUp, setPickedUp] = useState<boolean>(item?.pickedUp ?? false);
  const [savePin, setSavePin] = useState("");
  const [deletePin, setDeletePin] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [deleted, setDeleted] = useState(false);

  const swapCandidates = useSwapCandidates(fields.lastname, fields.name, fields.carMaker, mode === "create");

  const createMutation = useMutation({
    mutationFn: (employeeCode: string) =>
      createItem(client, {
        lastname: fields.lastname.trim(),
        name: fields.name.trim(),
        phone: fields.phone.trim(),
        carMaker: fields.carMaker.trim(),
        registrationNumber: fields.registrationNumber.trim(),
        tireSets: buildTireSets(),
        notes: fields.notes.trim(),
        washing: fields.washing,
        employeeCode,
        swapOutItemId: swapOutItemId ? asItemId(swapOutItemId) : null,
      }),
  });

  const updateMutation = useMutation({
    mutationFn: (employeeCode: string) =>
      updateItem(client, item!.id, {
        lastname: fields.lastname.trim(),
        name: fields.name.trim(),
        phone: fields.phone.trim(),
        carMaker: fields.carMaker.trim(),
        registrationNumber: fields.registrationNumber.trim(),
        tireSets: buildTireSets(),
        notes: fields.notes.trim(),
        washing: fields.washing,
        employeeCode,
      }),
  });

  const pickupMutation = useMutation({ mutationFn: (employeeCode: string) => markPickedUp(client, item!.id, employeeCode) });
  const revertPickupMutation = useMutation({ mutationFn: (employeeCode: string) => revertPickup(client, item!.id, employeeCode) });
  const deleteMutation = useMutation({ mutationFn: (employeeCode: string) => deleteItem(client, item!.id, employeeCode) });
  const restoreMutation = useMutation({ mutationFn: (employeeCode: string) => restoreItem(client, item!.id, employeeCode) });

  function updateField<K extends keyof FormFields>(key: K, value: FormFields[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  function buildTireSets(): NewItemInput["tireSets"] {
    const sets: NewItemInput["tireSets"] = [toApiTireSet(1, fields.set1)];
    if (fields.secondSetEnabled) sets.push(toApiTireSet(2, fields.set2));
    return sets;
  }

  async function invalidateListsAndItem() {
    await queryClient.invalidateQueries({ queryKey: ["items"] });
    if (item) await queryClient.invalidateQueries({ queryKey: ["item", item.id] });
  }

  async function runPrintIfRequested(target: WireItem): Promise<string | null> {
    if (!fields.printRequested) return null;
    // Chosen per save rather than once at module load: `capabilities` is
    // fetched, and the desktop bridge only exists inside the Electron shell.
    const printService = selectPrintService(capabilities.printing);
    const result = await printService.print({ item: target, requested: true }, capabilities.printing);
    return result.ok ? null : (result.message ?? "Tiskanje ni uspelo.");
  }

  async function handleSave() {
    setSaveError(null);
    setAttemptedSave(true);

    // Client-side required-field check first: flag the offending fields in place
    // (see `computeFieldErrors`) and jump focus to the first, rather than letting
    // the server reject the payload and rendering its list of failures below.
    const errors = computeFieldErrors(fields);
    if (firstInvalidId(errors) !== null) {
      setSaveError("Izpolnite označena polja.");
      document.getElementById(firstInvalidId(errors)!)?.focus();
      return;
    }

    // Mirrors legacy's own local pre-check (`getUserFromCode`, AddItemWindow.xaml.cs:301-305) —
    // exact same message, checked before any request is made.
    if (!savePin.trim()) {
      setSaveError("Manjka koda.");
      return;
    }

    try {
      if (mode === "create") {
        const result = await createMutation.mutateAsync(savePin.trim());
        await invalidateListsAndItem();
        // A brand-new deployment has no seasons at all until the first intake:
        // `createItem` lazily opens one (`getOrCreateOpenSeason`,
        // apps/server/src/repository/sqlite-repository.ts). Refresh the
        // seasons list too so the season selector picks it up immediately.
        await queryClient.invalidateQueries({ queryKey: ["seasons"] });
        const printMessage = await runPrintIfRequested(result.item);
        setConfirmation({ item: result.item, swappedOut: result.swappedOut, printMessage });
        return;
      }

      const updated = await updateMutation.mutateAsync(savePin.trim());
      let finalItem = updated.item;

      // Pickup state is a separate API surface from the general update (see
      // `UpdateItemPatch` — it excludes pickup/delete state entirely), so a
      // toggle here is a second request using the same PIN already entered
      // for the save. It can fail independently of the save that already
      // succeeded; that partial-failure case is surfaced explicitly rather
      // than swallowed.
      if (pickedUp !== (item?.pickedUp ?? false)) {
        try {
          const pickupResult = pickedUp
            ? await pickupMutation.mutateAsync(savePin.trim())
            : await revertPickupMutation.mutateAsync(savePin.trim());
          finalItem = pickupResult.item;
        } catch (pickupError) {
          await invalidateListsAndItem();
          setSaveError(`Podatki so bili shranjeni, sprememba stanja dviga pa ni uspela: ${describeApiError(pickupError)}`);
          return;
        }
      }

      await invalidateListsAndItem();
      const printMessage = await runPrintIfRequested(finalItem);
      setConfirmation({ item: finalItem, swappedOut: null, printMessage });
    } catch (error) {
      setSaveError(describeApiError(error));
    }
  }

  async function handleDelete() {
    setDeleteError(null);
    if (!deletePin.trim()) {
      setDeleteError("Manjka koda.");
      return;
    }
    try {
      await deleteMutation.mutateAsync(deletePin.trim());
      await invalidateListsAndItem();
      setDeleted(true);
    } catch (error) {
      setDeleteError(describeApiError(error));
    }
  }

  async function handleUndoDelete() {
    setDeleteError(null);
    try {
      await restoreMutation.mutateAsync(deletePin.trim());
      await invalidateListsAndItem();
      setDeleted(false);
    } catch (error) {
      setDeleteError(describeApiError(error));
    }
  }

  const saving = createMutation.isPending || updateMutation.isPending || pickupMutation.isPending || revertPickupMutation.isPending;

  // Only surface field errors once a save has been attempted, and recompute them
  // from the live fields so a red flag clears the moment its field is filled in.
  const fieldErrors: FieldErrors = attemptedSave ? computeFieldErrors(fields) : {};

  // Save/confirmation/delete are mutually exclusive views of the *same* modal
  // window (legacy's `AddItemWindow` opened with `ShowDialog()` over the list —
  // MainWindow.xaml.cs:255,283), so only ever one `role="dialog"` is mounted at
  // a time: the confirmation replaces the form rather than stacking on top.
  if (confirmation) {
    return (
      <Dialog title="Vnos shranjen" onClose={onDone}>
        <p>
          Številka: <strong>{confirmation.item.number}</strong>
        </p>
        {confirmation.swappedOut && <p>Prejšnji vnos št. {confirmation.swappedOut.number} je označen kot dvignjen.</p>}
        {confirmation.printMessage && <p className="form-print-note">{confirmation.printMessage}</p>}
        <div className="dialog-actions">
          <button type="button" onClick={onDone}>
            V redu
          </button>
        </div>
      </Dialog>
    );
  }

  if (deleted) {
    return (
      <Dialog title="Vnos izbrisan" onClose={onDone}>
        <p role="alert">Vnos št. {item?.number ?? ""} je izbrisan.</p>
        {deleteError && <p className="form-error">{deleteError}</p>}
        <div className="dialog-actions">
          <button type="button" onClick={() => void handleUndoDelete()} disabled={restoreMutation.isPending}>
            Razveljavi brisanje
          </button>
          <button type="button" onClick={onDone}>
            Nazaj na seznam
          </button>
        </div>
      </Dialog>
    );
  }

  const title = mode === "create" ? "Nov vnos" : `Urejanje vnosa št. ${item?.number ?? ""}`;

  return (
    // `dismissable={false}`: a backdrop click or Escape must not throw away a
    // half-filled entry — closing is only ever the explicit Prekliči / × / save,
    // matching legacy's window that closed only on Shrani/Preklici.
    <Dialog title={title} size="wide" dismissable={false} onClose={onDone}>
      <div className="item-form">
        {/* Identity + vehicle in two columns, with the read-only meta (number,
            date, season) to the right — the relative arrangement of
            AddItemWindow.xaml (Priimek/Ime, Znamka avta/Reg. številka, with
            Št./Datum/Sezona top-right). */}
        <div className="item-form-grid">
          <AutocompleteInput id="lastname" label="Priimek" field="lastname" uppercase required invalid={fieldErrors.lastname} value={fields.lastname} onChange={(v) => updateField("lastname", v)} />
          <AutocompleteInput id="name" label="Ime" field="name" uppercase required invalid={fieldErrors.name} value={fields.name} onChange={(v) => updateField("name", v)} />
          <AutocompleteInput id="carMaker" label="Znamka avta" field="carMaker" uppercase required invalid={fieldErrors.carMaker} value={fields.carMaker} onChange={(v) => updateField("carMaker", v)} />
          <AutocompleteInput
            id="registrationNumber"
            label="Reg. številka"
            field="registrationNumber"
            uppercase
            value={fields.registrationNumber}
            onChange={(v) => updateField("registrationNumber", v)}
          />
        </div>

        <div className="item-form-meta">
          <div className="form-field">
            <label htmlFor="display-number">Št.</label>
            <output id="display-number">{item?.number ?? "Dodeljena ob shranjevanju"}</output>
          </div>
          <div className="form-field">
            <label htmlFor="display-date">Datum</label>
            <output id="display-date">{item ? formatDateTime(item.receivedAt) : "Dodeljen ob shranjevanju"}</output>
          </div>
          <div className="form-field">
            <label htmlFor="display-season">Sezona</label>
            <output id="display-season">{seasonDisplayLabel(item, seasonsQuery.data)}</output>
          </div>
        </div>

        <TireSetFields ordinal={1} idPrefix="set1" value={fields.set1} onChange={(v) => updateField("set1", v)} invalid={fieldErrors.set1} />

        <label className="second-set-toggle">
          <input
            type="checkbox"
            checked={fields.secondSetEnabled}
            onChange={(e) => updateField("secondSetEnabled", e.target.checked)}
          />
          Druga garnitura
        </label>
        <TireSetFields
          ordinal={2}
          idPrefix="set2"
          value={fields.set2}
          onChange={(v) => updateField("set2", v)}
          disabled={!fields.secondSetEnabled}
          invalid={fieldErrors.set2}
        />

        {/* Opombe and Telefon share a row, as in the legacy layout (both on the
            AddItemWindow row at y≈182). Phone is never uppercased — legacy's
            `getItemFromFields` skips `.ToUpper()` for it. */}
        <div className="item-form-notes-row">
          <div className="form-field">
            <label htmlFor="notes">Opombe</label>
            <input id="notes" value={fields.notes} onChange={(e) => updateField("notes", e.target.value.toUpperCase())} />
          </div>
          <AutocompleteInput id="phone" label="Telefon" field="phone" value={fields.phone} onChange={(v) => updateField("phone", v)} />
        </div>

        <div className="form-checkboxes">
          <label>
            <input type="checkbox" checked={fields.washing} onChange={(e) => updateField("washing", e.target.checked)} />
            Pranje
          </label>
          <label>
            <input
              type="checkbox"
              checked={fields.printRequested}
              onChange={(e) => updateField("printRequested", e.target.checked)}
            />
            Tiskaj potrdilo
          </label>
          {mode === "edit" && (
            <label>
              <input type="checkbox" checked={pickedUp} onChange={(e) => setPickedUp(e.target.checked)} />
              Dvignjeno
            </label>
          )}
        </div>

        {mode === "create" && <SwapPicker candidates={swapCandidates} selectedId={swapOutItemId} onSelect={setSwapOutItemId} />}

        {saveError && (
          <p role="alert" className="form-error">
            {saveError}
          </p>
        )}

        {/* Bottom action bar: delete on the left, save on the right, each with
            its own PIN box — the same two-code, save-right/delete-left layout as
            AddItemWindow's footer (`zbrisiButton`/`saveButton`). */}
        <div className="item-form-footer">
          {mode === "edit" ? (
            <div className="item-form-delete">
              {deleteError && (
                <p role="alert" className="form-error">
                  {deleteError}
                </p>
              )}
              <PinField
                id="delete-pin"
                label="Koda za brisanje"
                value={deletePin}
                onChange={setDeletePin}
                onSubmit={() => void handleDelete()}
                disabled={deleteMutation.isPending}
              />
              <button type="button" className="danger" onClick={() => void handleDelete()} disabled={deleteMutation.isPending}>
                Izbriši
              </button>
            </div>
          ) : (
            <span />
          )}

          <div className="item-form-actions">
            <button type="button" className="item-form-cancel" onClick={onDone}>
              Prekliči
            </button>
            <PinField id="save-pin" label="Koda" value={savePin} onChange={setSavePin} onSubmit={() => void handleSave()} disabled={saving} />
            <button type="button" onClick={() => void handleSave()} disabled={saving}>
              Shrani
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
